import React, { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import Link from 'next/link'

import { Text } from 'components/atomic'
import { dkProductBundles, dkProductCrate } from 'lib/constants'
import { DkProductCrateDropNames } from 'constants/dkProductCrate/dkProductCrateDropNames'
import InventoryItemOpenModal from './inventoryItemOpenModal'
import LootItem from './lootItem'

interface ILootManagement {
  hasDayZeroPatch?: boolean
  paidProducts: Array<any>
  refetchInventoryData: () => void
  hasGameAccess: boolean
}

export interface InventoryItem {
  productName: string
  isDayZeroPatch: boolean
  isDkCrate: boolean
  isDkBundle: boolean
}

export default function LootManagement({
  hasDayZeroPatch,
  paidProducts,
  refetchInventoryData,
  hasGameAccess,
}: ILootManagement) {
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([])
  const [isInventoryItemOpenModal, setIsInventoryItemOpenModal] = useState(false)
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<InventoryItem>()

  const dkCratesCount = useMemo(() => {
    return inventoryItems.filter((item) => item.isDkCrate).length
  }, [inventoryItems])

  useEffect(() => {
    const tempInventoryItems: InventoryItem[] = []

    // Parsing paid product
    if (paidProducts.length > 0) {
      paidProducts.map((prod) => {
        const productName: string = prod.product_name
        const productQuantity: number = prod.quantity
        const productFlag: any = prod.flags

        if ('subItems' in productFlag && productName in dkProductBundles) {
          const _subItems = dkProductBundles[productName].subItems
          for (let i = 0; i < productQuantity; i++) {
            tempInventoryItems.push(
              ..._subItems.map((prodName) => ({
                productName: prodName,
                isDayZeroPatch: false,
                isDkCrate: false,
                isDkBundle: true,
              })),
            )
          }
        } else if ([...DkProductCrateDropNames, dkProductCrate].includes(productName)) {
          const isDkCrate = productName.localeCompare(dkProductCrate) === 0
          for (let i = 0; i < productQuantity; i++) {
            tempInventoryItems.push({
              productName,
              isDayZeroPatch: false,
              isDkCrate,
              isDkBundle: false,
            })
          }
        }
      })
    }

    // day zero patch, at end of array as it should be oldest
    if (hasDayZeroPatch) {
      tempInventoryItems.push({
        productName: 'DayZeroClawPatch',
        isDayZeroPatch: true,
        isDkCrate: false,
        isDkBundle: false,
      })
    }

    setInventoryItems(tempInventoryItems)
  }, [paidProducts, hasDayZeroPatch])

  useEffect(() => {
    if (dkCratesCount === 0) {
      handleCloseDkCrateModal()
    }
  }, [dkCratesCount])

  const handleShowDkCrateModal = (inventoryItem) => {
    setSelectedInventoryItem(inventoryItem)
    setIsInventoryItemOpenModal(true)
  }
  const handleCloseDkCrateModal = () => {
    setSelectedInventoryItem(undefined)
    setIsInventoryItemOpenModal(false)
  }

  const refreshProducts = (productOpened) => {
    setInventoryItems((prev) => [productOpened, ..._.without(prev, _.find(prev, { isDkCrate: true }))])
  }

  return (
    <>
      {/* Content */}
      <div className="flex w-full flex-col items-center justify-center space-y-6 lg:space-y-10">
        {/* Sorting */}
        <div className="mr-2 self-end lg:mr-16">
          <span className="font-tactic-extended text-sm tracking-widest text-white opacity-70">{'Newest'}</span>
        </div>
        {!hasGameAccess && (
          <Text variant="h4" className="text-center">
            <Text variant="h5" className="my-6">
              You have no DEADROP Game Access
            </Text>
            <Link href="/deadrop" passHref>
              <Text variant="h4" className="cursor-pointer text-secondary hover:underline" tag="span">
                Get it here!
              </Text>
            </Link>
            {/* <Text variant="h5" className="my-6">
            while supplies last
          </Text> */}
          </Text>
        )}
        {/* Items shown */}
        {inventoryItems.length > 0 && (
          <div className="grid w-full grid-cols-1 gap-x-2 gap-y-2 pb-2 2xl:gap-x-4 ip-x:grid-cols-2 ip-x:gap-y-5 nav:grid-cols-3 2lg:grid-cols-4 2lg:gap-x-4 2lg:gap-y-4 3xl:w-[calc(250px*5+16px*4)] 3xl:grid-cols-5 3xl:gap-y-4">
            {inventoryItems.map((item, index) => (
              <LootItem key={index} id={index} item={item} onShowCrateOpenDialog={handleShowDkCrateModal} />
            ))}
          </div>
        )}
      </div>
      {/* Open InventoryItemModal */}
      {isInventoryItemOpenModal && selectedInventoryItem && (
        <InventoryItemOpenModal
          show={isInventoryItemOpenModal}
          item={selectedInventoryItem}
          onOpened={refreshProducts}
          onCloseModal={handleCloseDkCrateModal}
          dkCratesCount={dkCratesCount}
          refetchInventoryData={refetchInventoryData}
        />
      )}
    </>
  )
}
